/* eslint-disable @typescript-eslint/no-explicit-any */

class StrictGpxParser {
  xmlSource: any;
  metadata: Record<string, any>;
  waypoints: Array<Record<string, any>>;
  tracks: Array<Record<string, any>>;
  routes: Array<Record<string, any>>;

  constructor() {
    this.xmlSource = "";
    this.metadata = {};
    this.waypoints = [];
    this.tracks = [];
    this.routes = [];
  }

  parse(gpxstring: string): StrictGpxParser {
    const keepThis = this;

    const domParser = new window.DOMParser();
    this.xmlSource = domParser.parseFromString(gpxstring, "text/xml");

    const metadata = this.xmlSource.querySelector("metadata");
    if (metadata != null) {
      this.metadata.name = this.getElementValue(metadata, "name");
      this.metadata.desc = this.getElementValue(metadata, "desc");
      this.metadata.time = this.getElementValue(metadata, "time");

      const author: Record<string, any> = {};
      const authorElem = metadata.querySelector("author");
      if (authorElem != null) {
        author.name = this.getElementValue(authorElem, "name");
        author.email = {};
        const emailElem = authorElem.querySelector("email");
        if (emailElem != null) {
          author.email.id = emailElem.getAttribute("id");
          author.email.domain = emailElem.getAttribute("domain");
        }

        const link: Record<string, any> = {};
        const linkElem = authorElem.querySelector("link");
        if (linkElem != null) {
          link.href = linkElem.getAttribute("href");
          link.text = this.getElementValue(linkElem, "text");
          link.type = this.getElementValue(linkElem, "type");
        }
        author.link = link;
      }
      this.metadata.author = author;

      const link: Record<string, any> = {};
      const linkElem = this.queryDirectSelector(metadata, "link");
      if (linkElem != null) {
        link.href = linkElem.getAttribute("href");
        link.text = this.getElementValue(linkElem, "text");
        link.type = this.getElementValue(linkElem, "type");
        this.metadata.link = link;
      }
    }

    const wpts = Array.prototype.slice.call(this.xmlSource.querySelectorAll("wpt"));
    for (let idx = 0; idx < wpts.length; idx += 1) {
      const wpt = wpts[idx];
      const pt: Record<string, any> = {};
      pt.name = keepThis.getElementValue(wpt, "name");
      pt.sym = keepThis.getElementValue(wpt, "sym");
      pt.lat = parseFloat(wpt.getAttribute("lat"));
      pt.lon = parseFloat(wpt.getAttribute("lon"));

      const floatValue = parseFloat(keepThis.getElementValue(wpt, "ele"));
      pt.ele = Number.isNaN(floatValue) ? null : floatValue;

      pt.cmt = keepThis.getElementValue(wpt, "cmt");
      pt.desc = keepThis.getElementValue(wpt, "desc");

      const time = keepThis.getElementValue(wpt, "time");
      pt.time = time == null ? null : new Date(time);

      keepThis.waypoints.push(pt);
    }

    const rtes = Array.prototype.slice.call(this.xmlSource.querySelectorAll("rte"));
    for (let idx = 0; idx < rtes.length; idx += 1) {
      const rte = rtes[idx];
      const route: Record<string, any> = {};
      route.name = keepThis.getElementValue(rte, "name");
      route.cmt = keepThis.getElementValue(rte, "cmt");
      route.desc = keepThis.getElementValue(rte, "desc");
      route.src = keepThis.getElementValue(rte, "src");
      route.number = keepThis.getElementValue(rte, "number");

      const type = keepThis.queryDirectSelector(rte, "type");
      route.type = type != null ? type.innerHTML : null;

      const link: Record<string, any> = {};
      const linkElem = rte.querySelector("link");
      if (linkElem != null) {
        link.href = linkElem.getAttribute("href");
        link.text = keepThis.getElementValue(linkElem, "text");
        link.type = keepThis.getElementValue(linkElem, "type");
      }
      route.link = link;

      const routepoints: Array<Record<string, any>> = [];
      const rtepts = Array.prototype.slice.call(rte.querySelectorAll("rtept"));

      for (let idxIn = 0; idxIn < rtepts.length; idxIn += 1) {
        const rtept = rtepts[idxIn];
        const pt: Record<string, any> = {};
        pt.lat = parseFloat(rtept.getAttribute("lat"));
        pt.lon = parseFloat(rtept.getAttribute("lon"));

        const floatValue = parseFloat(keepThis.getElementValue(rtept, "ele"));
        pt.ele = Number.isNaN(floatValue) ? null : floatValue;

        const time = keepThis.getElementValue(rtept, "time");
        pt.time = time == null ? null : new Date(time);

        routepoints.push(pt);
      }

      route.distance = keepThis.calculDistance(routepoints);
      route.elevation = keepThis.calcElevation(routepoints);
      route.slopes = keepThis.calculSlope(routepoints, route.distance.cumul);
      route.points = routepoints;

      keepThis.routes.push(route);
    }

    const trks = Array.prototype.slice.call(this.xmlSource.querySelectorAll("trk"));
    for (let idx = 0; idx < trks.length; idx += 1) {
      const trk = trks[idx];
      const track: Record<string, any> = {};

      track.name = keepThis.getElementValue(trk, "name");
      track.cmt = keepThis.getElementValue(trk, "cmt");
      track.desc = keepThis.getElementValue(trk, "desc");
      track.src = keepThis.getElementValue(trk, "src");
      track.number = keepThis.getElementValue(trk, "number");

      const type = keepThis.queryDirectSelector(trk, "type");
      track.type = type != null ? type.innerHTML : null;

      const link: Record<string, any> = {};
      const linkElem = trk.querySelector("link");
      if (linkElem != null) {
        link.href = linkElem.getAttribute("href");
        link.text = keepThis.getElementValue(linkElem, "text");
        link.type = keepThis.getElementValue(linkElem, "type");
      }
      track.link = link;

      const trackpoints: Array<Record<string, any>> = [];
      const trkpts = Array.prototype.slice.call(trk.querySelectorAll("trkpt"));
      for (let idxIn = 0; idxIn < trkpts.length; idxIn += 1) {
        const trkpt = trkpts[idxIn];
        const pt: Record<string, any> = {};
        pt.lat = parseFloat(trkpt.getAttribute("lat"));
        pt.lon = parseFloat(trkpt.getAttribute("lon"));

        const floatValue = parseFloat(keepThis.getElementValue(trkpt, "ele"));
        pt.ele = Number.isNaN(floatValue) ? null : floatValue;

        const time = keepThis.getElementValue(trkpt, "time");
        pt.time = time == null ? null : new Date(time);

        trackpoints.push(pt);
      }
      track.distance = keepThis.calculDistance(trackpoints);
      track.elevation = keepThis.calcElevation(trackpoints);
      track.slopes = keepThis.calculSlope(trackpoints, track.distance.cumul);
      track.points = trackpoints;

      keepThis.tracks.push(track);
    }

    return this;
  }

  getElementValue(parent: any, needle: string): any {
    const elem = parent.querySelector(needle);
    if (elem != null) {
      return elem.innerHTML !== undefined ? elem.innerHTML : elem.childNodes[0].data;
    }
    return elem;
  }

  queryDirectSelector(parent: any, needle: string): any {
    const elements = parent.querySelectorAll(needle);
    let finalElem = elements[0];

    if (elements.length > 1) {
      const directChilds = parent.childNodes;

      for (let idx = 0; idx < directChilds.length; idx += 1) {
        const elem = directChilds[idx];
        if (elem.tagName === needle) {
          finalElem = elem;
        }
      }
    }

    return finalElem;
  }

  calculDistance(points: Array<Record<string, any>>): Record<string, any> {
    const distance: Record<string, any> = {};
    let totalDistance = 0;
    const cumulDistance: number[] = [];
    for (let i = 0; i < points.length - 1; i += 1) {
      totalDistance += this.calcDistanceBetween(points[i], points[i + 1]);
      cumulDistance[i] = totalDistance;
    }
    cumulDistance[points.length - 1] = totalDistance;

    distance.total = totalDistance;
    distance.cumul = cumulDistance;

    return distance;
  }

  calcDistanceBetween(wpt1: Record<string, any>, wpt2: Record<string, any>): number {
    const latlng1: Record<string, number> = {};
    latlng1.lat = wpt1.lat;
    latlng1.lon = wpt1.lon;
    const latlng2: Record<string, number> = {};
    latlng2.lat = wpt2.lat;
    latlng2.lon = wpt2.lon;
    const rad = Math.PI / 180;
    const lat1 = latlng1.lat * rad;
    const lat2 = latlng2.lat * rad;
    const sinDLat = Math.sin(((latlng2.lat - latlng1.lat) * rad) / 2);
    const sinDLon = Math.sin(((latlng2.lon - latlng1.lon) * rad) / 2);
    const a = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return 6371000 * c;
  }

  calcElevation(points: Array<Record<string, any>>): Record<string, any> {
    let dp = 0;
    let dm = 0;
    const ret: Record<string, any> = {};

    for (let i = 0; i < points.length - 1; i += 1) {
      const rawNextElevation = points[i + 1].ele;
      const rawElevation = points[i].ele;

      if (rawNextElevation !== null && rawElevation !== null) {
        const diff = parseFloat(rawNextElevation) - parseFloat(rawElevation);

        if (diff < 0) {
          dm += diff;
        } else if (diff > 0) {
          dp += diff;
        }
      }
    }

    const elevation: number[] = [];
    let sum = 0;

    for (let i = 0, len = points.length; i < len; i += 1) {
      const rawElevation = points[i].ele;

      if (rawElevation !== null) {
        const ele = parseFloat(points[i].ele);
        elevation.push(ele);
        sum += ele;
      }
    }

    ret.max = elevation.length > 0 ? Math.max.apply(null, elevation) : null;
    ret.min = elevation.length > 0 ? Math.min.apply(null, elevation) : null;
    ret.pos = Math.abs(dp) || null;
    ret.neg = Math.abs(dm) || null;
    ret.avg = elevation.length > 0 ? sum / elevation.length : null;

    return ret;
  }

  calculSlope(points: Array<Record<string, any>>, cumul: number[]): number[] {
    const slopes: number[] = [];

    for (let i = 0; i < points.length - 1; i += 1) {
      const point = points[i];
      const nextPoint = points[i + 1];
      const elevationDiff = nextPoint.ele - point.ele;
      const distance = cumul[i + 1] - cumul[i];

      const slope = (elevationDiff * 100) / distance;
      slopes.push(slope);
    }

    return slopes;
  }

  toGeoJSON(): Record<string, any> {
    const GeoJSON: Record<string, any> = {
      type: "FeatureCollection",
      features: [],
      properties: {
        name: this.metadata.name,
        desc: this.metadata.desc,
        time: this.metadata.time,
        author: this.metadata.author,
        link: this.metadata.link,
      },
    };

    for (let idx = 0; idx < this.tracks.length; idx += 1) {
      const track = this.tracks[idx];

      const feature: Record<string, any> = {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [],
        },
        properties: {},
      };

      feature.properties.name = track.name;
      feature.properties.cmt = track.cmt;
      feature.properties.desc = track.desc;
      feature.properties.src = track.src;
      feature.properties.number = track.number;
      feature.properties.link = track.link;
      feature.properties.type = track.type;

      for (let idxPoint = 0; idxPoint < track.points.length; idxPoint += 1) {
        const pt = track.points[idxPoint];

        const geoPt: number[] = [];
        geoPt.push(pt.lon);
        geoPt.push(pt.lat);
        geoPt.push(pt.ele);

        feature.geometry.coordinates.push(geoPt);
      }

      GeoJSON.features.push(feature);
    }

    for (let idx = 0; idx < this.routes.length; idx += 1) {
      const track = this.routes[idx];

      const feature: Record<string, any> = {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [],
        },
        properties: {},
      };

      feature.properties.name = track.name;
      feature.properties.cmt = track.cmt;
      feature.properties.desc = track.desc;
      feature.properties.src = track.src;
      feature.properties.number = track.number;
      feature.properties.link = track.link;
      feature.properties.type = track.type;

      for (let idxPoint = 0; idxPoint < track.points.length; idxPoint += 1) {
        const pt = track.points[idxPoint];

        const geoPt: number[] = [];
        geoPt.push(pt.lon);
        geoPt.push(pt.lat);
        geoPt.push(pt.ele);

        feature.geometry.coordinates.push(geoPt);
      }

      GeoJSON.features.push(feature);
    }

    for (let idx = 0; idx < this.waypoints.length; idx += 1) {
      const pt = this.waypoints[idx];

      const feature: Record<string, any> = {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [],
        },
        properties: {},
      };

      feature.properties.name = pt.name;
      feature.properties.sym = pt.sym;
      feature.properties.cmt = pt.cmt;
      feature.properties.desc = pt.desc;

      feature.geometry.coordinates = [pt.lon, pt.lat, pt.ele];

      GeoJSON.features.push(feature);
    }

    return GeoJSON;
  }
}

export default StrictGpxParser;
